import { API_BASE_URL } from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Target, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Printer,
  ChevronDown,
  ChevronRight,
  Flag,
  CheckCircle
} from 'lucide-react';
import secureStorage from './utils/secureStorage';
import { writeSanitizedHTML } from './utils/safePrint';

const API = API_BASE_URL;

const HierarchicalObjectives = ({ theme, exerciseId, exerciseName }) => {
  // ICS Positions List
  const positions = [
    'Command - Incident Commander',
    'Command - Incident Commander (deputy)',
    'Command - Safety Officer',
    'Command - Information Officer',
    'Command - Liaison Officer',
    'Operations - Section Chief',
    'Operations - Air Ops Branch Director',
    'Operations - Branch 1 Director',
    'Operations - Branch 2 Director',
    'Operations - Air Support Group Supervisor',
    'Operations - Helibase Supervisor',
    'Operations - Helispot Supervisor',
    'Operations - Air Tactical Group Supervisor',
    'Operations - Strike Team Leader',
    'Operations - Task Force Leader',
    'Operations - Single Resource Boss',
    'Planning - Section Chief',
    'Planning - Resource Unit Leader',
    'Planning - Situation Unit Leader',
    'Planning - Documentation Unit Leader',
    'Planning - Demobilization Unit Leader',
    'Planning - Technical Specialist',
    'Logistics - Section Chief',
    'Logistics - Support Branch Director',
    'Logistics - Service Branch Director',
    'Logistics - Supply Unit Leader',
    'Logistics - Facilities Unit Leader',
    'Logistics - Ground Support Unit Leader',
    'Logistics - Communications Unit Leader',
    'Logistics - Food Unit Leader',
    'Logistics - Medical Unit Leader',
    'Finance - Section Chief',
    'Finance - Time Unit Leader',
    'Finance - Procurement Unit Leader',
    'Finance - Compensation Claims Unit Leader',
    'Finance - Cost Unit Leader'
  ];

  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());
  const [expandedStrategies, setExpandedStrategies] = useState(new Set());
  
  // Editing states
  const [editingObjective, setEditingObjective] = useState(null);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [editingTactic, setEditingTactic] = useState(null);
  
  // Form states
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [showStrategyForm, setShowStrategyForm] = useState({ show: false, objectiveId: null });
  const [showTacticForm, setShowTacticForm] = useState({ show: false, strategyId: null });
  
  // Form data
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    assigned_to: '',
    deadline: ''
  });
  
  const [strategyForm, setStrategyForm] = useState({
    title: '',
    description: '',
    resource_requirements: '',
    timeline: ''
  });
  
  const [tacticForm, setTacticForm] = useState({
    action: '',
    responsible_party: '',
    completion_date: '',
    status: 'pending',
    notes: ''
  });

  // Load objectives data
  useEffect(() => {
    if (exerciseId) {
      loadObjectives();
    }
  }, [exerciseId]);

  const loadObjectives = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/api/hierarchical-objectives?exercise_id=${exerciseId}`,
        { headers }
      );
      setObjectives(response.data || []);
    } catch (error) {
      console.error('Error loading objectives:', error);
      setObjectives([]);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations for Objectives
  const saveObjective = async () => {
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to save objectives');
        return;
      }
      
      if (!exerciseId) {
        alert('Error: No exercise selected. Please select an exercise first.');
        return;
      }
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const data = {
        ...objectiveForm,
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        strategies: []
      };

      if (editingObjective) {
        await axios.put(
          `${API}/api/hierarchical-objectives/${editingObjective.id}`,
          data,
          { headers }
        );
      } else {
        await axios.post(`${API}/api/hierarchical-objectives`, data, { headers });
      }
      
      loadObjectives();
      resetObjectiveForm();
    } catch (error) {
      console.error('Error saving objective:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error saving objective: ${errorMessage}`);
    }
  };

  const deleteObjective = async (objectiveId) => {
    if (!window.confirm('Are you sure you want to delete this objective and all its strategies and tactics?')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to delete objectives');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/hierarchical-objectives/${objectiveId}`, { headers });
      loadObjectives();
    } catch (error) {
      console.error('Error deleting objective:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error deleting objective: ${errorMessage}`);
    }
  };

  // CRUD Operations for Strategies
  const saveStrategy = async (objectiveId) => {
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to save strategies');
        return;
      }
      
      if (!objectiveId) {
        alert('Error: No objective selected. Please select an objective first.');
        return;
      }
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const data = {
        ...strategyForm,
        objective_id: objectiveId,
        tactics: []
      };

      if (editingStrategy) {
        await axios.put(
          `${API}/api/hierarchical-strategies/${editingStrategy.id}`,
          data,
          { headers }
        );
      } else {
        await axios.post(`${API}/api/hierarchical-strategies`, data, { headers });
      }
      
      loadObjectives();
      resetStrategyForm();
    } catch (error) {
      console.error('Error saving strategy:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error saving strategy: ${errorMessage}`);
    }
  };

  const deleteStrategy = async (strategyId) => {
    if (!window.confirm('Are you sure you want to delete this strategy and all its tactics?')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to delete strategies');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/hierarchical-strategies/${strategyId}`, { headers });
      loadObjectives();
    } catch (error) {
      console.error('Error deleting strategy:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error deleting strategy: ${errorMessage}`);
    }
  };

  // CRUD Operations for Tactics
  const saveTactic = async (strategyId) => {
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to save tactics');
        return;
      }
      
      if (!strategyId) {
        alert('Error: No strategy selected. Please select a strategy first.');
        return;
      }
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const data = {
        ...tacticForm,
        strategy_id: strategyId
      };

      if (editingTactic) {
        await axios.put(
          `${API}/api/hierarchical-tactics/${editingTactic.id}`,
          data,
          { headers }
        );
      } else {
        await axios.post(`${API}/api/hierarchical-tactics`, data, { headers });
      }
      
      loadObjectives();
      resetTacticForm();
    } catch (error) {
      console.error('Error saving tactic:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error saving tactic: ${errorMessage}`);
    }
  };

  const deleteTactic = async (tacticId) => {
    if (!window.confirm('Are you sure you want to delete this tactic?')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to delete tactics');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/hierarchical-tactics/${tacticId}`, { headers });
      loadObjectives();
    } catch (error) {
      console.error('Error deleting tactic:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error deleting tactic: ${errorMessage}`);
    }
  };

  // Reset forms
  const resetObjectiveForm = () => {
    setObjectiveForm({
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      assigned_to: '',
      deadline: ''
    });
    setEditingObjective(null);
    setShowObjectiveForm(false);
  };

  const resetStrategyForm = () => {
    setStrategyForm({
      title: '',
      description: '',
      resource_requirements: '',
      timeline: ''
    });
    setEditingStrategy(null);
    setShowStrategyForm({ show: false, objectiveId: null });
  };

  const resetTacticForm = () => {
    setTacticForm({
      action: '',
      responsible_party: '',
      completion_date: '',
      status: 'pending',
      notes: ''
    });
    setEditingTactic(null);
    setShowTacticForm({ show: false, strategyId: null });
  };

  // Edit handlers
  const startEditObjective = (objective) => {
    setEditingObjective(objective);
    setObjectiveForm({
      title: objective.title,
      description: objective.description,
      priority: objective.priority,
      status: objective.status,
      assigned_to: objective.assigned_to || '',
      deadline: objective.deadline || ''
    });
    setShowObjectiveForm(true);
  };

  const startEditStrategy = (strategy, objectiveId) => {
    setEditingStrategy(strategy);
    setStrategyForm({
      title: strategy.title,
      description: strategy.description,
      resource_requirements: strategy.resource_requirements || '',
      timeline: strategy.timeline || ''
    });
    setShowStrategyForm({ show: true, objectiveId });
  };

  const startEditTactic = (tactic, strategyId) => {
    setEditingTactic(tactic);
    setTacticForm({
      action: tactic.action,
      responsible_party: tactic.responsible_party || '',
      completion_date: tactic.completion_date || '',
      status: tactic.status,
      notes: tactic.notes || ''
    });
    setShowTacticForm({ show: true, strategyId });
  };

  // Toggle expansion
  const toggleObjective = (objectiveId) => {
    const newExpanded = new Set(expandedObjectives);
    if (newExpanded.has(objectiveId)) {
      newExpanded.delete(objectiveId);
    } else {
      newExpanded.add(objectiveId);
    }
    setExpandedObjectives(newExpanded);
  };

  const toggleStrategy = (strategyId) => {
    const newExpanded = new Set(expandedStrategies);
    if (newExpanded.has(strategyId)) {
      newExpanded.delete(strategyId);
    } else {
      newExpanded.add(strategyId);
    }
    setExpandedStrategies(newExpanded);
  };

  // Print function
  const printObjectives = () => {
    const printContent = `
      <html>
        <head>
          <title>Incident Objectives - ${exerciseName || 'Exercise'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
            .objective { margin-bottom: 30px; border: 2px solid #333; padding: 15px; }
            .strategy { margin-left: 30px; margin-bottom: 20px; border: 1px solid #666; padding: 10px; }
            .tactic { margin-left: 60px; margin-bottom: 10px; border: 1px solid #999; padding: 8px; background: #f9f9f9; }
            .title { font-weight: bold; font-size: 16px; margin-bottom: 5px; }
            .label { font-weight: bold; margin-right: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Incident Objectives</h1>
            <p><strong>Exercise:</strong> ${exerciseName || 'N/A'}</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
          ${objectives.map((obj, idx) => `
            <div class="objective">
              <div class="title">OBJECTIVE ${idx + 1}: ${obj.title}</div>
              <p><span class="label">Description:</span> ${obj.description || 'N/A'}</p>
              <p><span class="label">Priority:</span> ${obj.priority || 'N/A'}</p>
              <p><span class="label">Status:</span> ${obj.status || 'N/A'}</p>
              <p><span class="label">Assigned To:</span> ${obj.assigned_to || 'N/A'}</p>
              <p><span class="label">Deadline:</span> ${obj.deadline || 'N/A'}</p>
              
              ${obj.strategies?.map((strategy, sIdx) => `
                <div class="strategy">
                  <div class="title">Strategy ${idx + 1}.${sIdx + 1}: ${strategy.title}</div>
                  <p><span class="label">Description:</span> ${strategy.description || 'N/A'}</p>
                  <p><span class="label">Resources:</span> ${strategy.resource_requirements || 'N/A'}</p>
                  <p><span class="label">Timeline:</span> ${strategy.timeline || 'N/A'}</p>
                  
                  ${strategy.tactics?.map((tactic, tIdx) => `
                    <div class="tactic">
                      <div class="title">Tactic ${idx + 1}.${sIdx + 1}.${tIdx + 1}: ${tactic.action}</div>
                      <p><span class="label">Responsible Party:</span> ${tactic.responsible_party || 'N/A'}</p>
                      <p><span class="label">Completion Date:</span> ${tactic.completion_date || 'N/A'}</p>
                      <p><span class="label">Status:</span> ${tactic.status || 'N/A'}</p>
                      <p><span class="label">Notes:</span> ${tactic.notes || 'N/A'}</p>
                    </div>
                  `).join('')}
                </div>
              `).join('')}
            </div>
          `).join('')}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-500 border-green-500',
      medium: 'text-yellow-500 border-yellow-500',
      high: 'text-orange-500 border-orange-500',
      critical: 'text-red-500 border-red-500'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-500',
      pending: 'text-yellow-500',
      completed: 'text-blue-500',
      archived: 'text-gray-500'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Incident Objectives</h2>
          <p className="text-gray-400">
            Hierarchical objectives with strategies and tactics for {exerciseName || 'this exercise'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => window.open('#display-objectives', '_blank')}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
            title="Open in Display Mode"
          >
            <Printer className="h-4 w-4" />
            Display Mode
          </button>
          <button
            onClick={printObjectives}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print All
          </button>
          <button
            onClick={() => setShowObjectiveForm(true)}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Objective
          </button>
        </div>
      </div>

      {/* Objective Form Modal */}
      {showObjectiveForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                {editingObjective ? 'Edit Objective' : 'New Objective'}
              </h3>
              <button onClick={resetObjectiveForm} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">Title *</label>
                <input
                  type="text"
                  value={objectiveForm.title}
                  onChange={(e) => setObjectiveForm({ ...objectiveForm, title: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  placeholder="e.g., Ensure public safety in affected areas"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Description *</label>
                <textarea
                  value={objectiveForm.description}
                  onChange={(e) => setObjectiveForm({ ...objectiveForm, description: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  rows="4"
                  placeholder="Detailed description of the objective"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Priority</label>
                  <select
                    value={objectiveForm.priority}
                    onChange={(e) => setObjectiveForm({ ...objectiveForm, priority: e.target.value })}
                    className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white mb-2">Status</label>
                  <select
                    value={objectiveForm.status}
                    onChange={(e) => setObjectiveForm({ ...objectiveForm, status: e.target.value })}
                    className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Assigned To</label>
                <select
                  value={objectiveForm.assigned_to}
                  onChange={(e) => setObjectiveForm({ ...objectiveForm, assigned_to: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                >
                  <option value="">Select Position...</option>
                  <optgroup label="Command Staff">
                    {positions.filter(p => p.startsWith('Command -')).map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Operations Section">
                    {positions.filter(p => p.startsWith('Operations -')).map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Planning Section">
                    {positions.filter(p => p.startsWith('Planning -')).map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Logistics Section">
                    {positions.filter(p => p.startsWith('Logistics -')).map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Finance/Administration Section">
                    {positions.filter(p => p.startsWith('Finance -')).map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-white mb-2">Deadline</label>
                <input
                  type="date"
                  value={objectiveForm.deadline}
                  onChange={(e) => setObjectiveForm({ ...objectiveForm, deadline: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveObjective}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save Objective
              </button>
              <button
                onClick={resetObjectiveForm}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Form Modal */}
      {showStrategyForm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                {editingStrategy ? 'Edit Strategy' : 'New Strategy'}
              </h3>
              <button onClick={resetStrategyForm} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">Strategy Title *</label>
                <input
                  type="text"
                  value={strategyForm.title}
                  onChange={(e) => setStrategyForm({ ...strategyForm, title: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  placeholder="e.g., Establish evacuation zones"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Description *</label>
                <textarea
                  value={strategyForm.description}
                  onChange={(e) => setStrategyForm({ ...strategyForm, description: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  rows="3"
                  placeholder="Detailed description of the strategy"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Resource Requirements</label>
                <textarea
                  value={strategyForm.resource_requirements}
                  onChange={(e) => setStrategyForm({ ...strategyForm, resource_requirements: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  rows="2"
                  placeholder="Personnel, equipment, and materials needed"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Timeline</label>
                <input
                  type="text"
                  value={strategyForm.timeline}
                  onChange={(e) => setStrategyForm({ ...strategyForm, timeline: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  placeholder="e.g., Within 4 hours of incident start"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => saveStrategy(showStrategyForm.objectiveId)}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save Strategy
              </button>
              <button
                onClick={resetStrategyForm}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tactic Form Modal */}
      {showTacticForm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                {editingTactic ? 'Edit Tactic' : 'New Tactic'}
              </h3>
              <button onClick={resetTacticForm} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">Tactical Action *</label>
                <input
                  type="text"
                  value={tacticForm.action}
                  onChange={(e) => setTacticForm({ ...tacticForm, action: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  placeholder="e.g., Deploy mobile command post"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Responsible Party</label>
                <input
                  type="text"
                  value={tacticForm.responsible_party}
                  onChange={(e) => setTacticForm({ ...tacticForm, responsible_party: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  placeholder="e.g., Strike Team Alpha Leader"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Completion Date</label>
                  <input
                    type="date"
                    value={tacticForm.completion_date}
                    onChange={(e) => setTacticForm({ ...tacticForm, completion_date: e.target.value })}
                    className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Status</label>
                  <select
                    value={tacticForm.status}
                    onChange={(e) => setTacticForm({ ...tacticForm, status: e.target.value })}
                    className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Notes</label>
                <textarea
                  value={tacticForm.notes}
                  onChange={(e) => setTacticForm({ ...tacticForm, notes: e.target.value })}
                  className="w-full bg-gray-700 text-white rounded px-3 py-2"
                  rows="2"
                  placeholder="Additional notes or details"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => saveTactic(showTacticForm.strategyId)}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save Tactic
              </button>
              <button
                onClick={resetTacticForm}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Objectives List */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading objectives...</div>
      ) : objectives.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <Target className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>No objectives created yet. Click &quot;Add Objective&quot; to get started.</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4 pb-6">
          {objectives.map((objective, objIdx) => (
            <div key={objective.id} className={`bg-gray-800 border-2 ${getPriorityColor(objective.priority)} rounded-lg`}>
              {/* Objective Header */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <button
                      onClick={() => toggleObjective(objective.id)}
                      className="text-gray-400 hover:text-white mt-1"
                    >
                      {expandedObjectives.has(objective.id) ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                    </button>
                    <Target className={`h-6 w-6 mt-1 ${getPriorityColor(objective.priority)}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-white">
                          Objective {objIdx + 1}: {objective.title}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(objective.priority)} border`}>
                          {objective.priority}
                        </span>
                        <span className={`text-xs ${getStatusColor(objective.status)}`}>
                          {objective.status}
                        </span>
                      </div>
                      {expandedObjectives.has(objective.id) && (
                        <div className="space-y-2 text-sm">
                          <p className="text-gray-300">{objective.description}</p>
                          {objective.assigned_to && (
                            <p className="text-gray-400">
                              <span className="font-semibold">Assigned to:</span> {objective.assigned_to}
                            </p>
                          )}
                          {objective.deadline && (
                            <p className="text-gray-400">
                              <span className="font-semibold">Deadline:</span> {objective.deadline}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowStrategyForm({ show: true, objectiveId: objective.id })}
                      className="text-green-500 hover:text-green-400"
                      title="Add Strategy"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => startEditObjective(objective)}
                      className="text-blue-500 hover:text-blue-400"
                      title="Edit Objective"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => deleteObjective(objective.id)}
                      className="text-red-500 hover:text-red-400"
                      title="Delete Objective"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Strategies */}
              {expandedObjectives.has(objective.id) && objective.strategies && objective.strategies.length > 0 && (
                <div className="ml-12 mr-4 mb-4 space-y-3">
                  {objective.strategies.map((strategy, stratIdx) => (
                    <div key={strategy.id} className="bg-gray-700 border border-gray-600 rounded-lg">
                      <div className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <button
                              onClick={() => toggleStrategy(strategy.id)}
                              className="text-gray-400 hover:text-white mt-1"
                            >
                              {expandedStrategies.has(strategy.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <Flag className="h-5 w-5 text-yellow-500 mt-1" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-white mb-1">
                                Strategy {objIdx + 1}.{stratIdx + 1}: {strategy.title}
                              </h4>
                              {expandedStrategies.has(strategy.id) && (
                                <div className="space-y-1 text-sm">
                                  <p className="text-gray-300">{strategy.description}</p>
                                  {strategy.resource_requirements && (
                                    <p className="text-gray-400">
                                      <span className="font-semibold">Resources:</span> {strategy.resource_requirements}
                                    </p>
                                  )}
                                  {strategy.timeline && (
                                    <p className="text-gray-400">
                                      <span className="font-semibold">Timeline:</span> {strategy.timeline}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowTacticForm({ show: true, strategyId: strategy.id })}
                              className="text-green-500 hover:text-green-400"
                              title="Add Tactic"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => startEditStrategy(strategy, objective.id)}
                              className="text-blue-500 hover:text-blue-400"
                              title="Edit Strategy"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteStrategy(strategy.id)}
                              className="text-red-500 hover:text-red-400"
                              title="Delete Strategy"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Tactics */}
                      {expandedStrategies.has(strategy.id) && strategy.tactics && strategy.tactics.length > 0 && (
                        <div className="ml-10 mr-3 mb-3 space-y-2">
                          {strategy.tactics.map((tactic, tactIdx) => (
                            <div key={tactic.id} className="bg-gray-600 border border-gray-500 rounded p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-2 flex-1">
                                  <CheckCircle className={`h-4 w-4 mt-1 ${
                                    tactic.status === 'completed' ? 'text-green-500' : 
                                    tactic.status === 'in-progress' ? 'text-yellow-500' : 
                                    'text-gray-400'
                                  }`} />
                                  <div className="flex-1">
                                    <p className="font-semibold text-white text-sm mb-1">
                                      Tactic {objIdx + 1}.{stratIdx + 1}.{tactIdx + 1}: {tactic.action}
                                    </p>
                                    <div className="space-y-1 text-xs text-gray-300">
                                      {tactic.responsible_party && (
                                        <p><span className="font-semibold">Responsible:</span> {tactic.responsible_party}</p>
                                      )}
                                      {tactic.completion_date && (
                                        <p><span className="font-semibold">Due:</span> {tactic.completion_date}</p>
                                      )}
                                      <p>
                                        <span className="font-semibold">Status:</span>{' '}
                                        <span className={
                                          tactic.status === 'completed' ? 'text-green-400' : 
                                          tactic.status === 'in-progress' ? 'text-yellow-400' : 
                                          'text-gray-400'
                                        }>
                                          {tactic.status}
                                        </span>
                                      </p>
                                      {tactic.notes && (
                                        <p className="text-gray-400">{tactic.notes}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => startEditTactic(tactic, strategy.id)}
                                    className="text-blue-500 hover:text-blue-400"
                                    title="Edit Tactic"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => deleteTactic(tactic.id)}
                                    className="text-red-500 hover:text-red-400"
                                    title="Delete Tactic"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchicalObjectives;
